import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import type { ErrorProviderError, GenericError, IErrorMutator } from '../types';

export class AxiosMutator implements IErrorMutator {
  private async getErrorDataFromStream(responseStream): Promise<any> {
    return new Promise((resolve) => {
      const responseBuffer: any[] = [];

      responseStream.on('data', (chunk) => {
        responseBuffer.push(chunk);
      });

      responseStream.on('aborted', () => {
        resolve({ message: 'Unknown error' });
      });

      responseStream.on('error', () => {
        resolve({ message: 'Unknown error' });
      });

      responseStream.on('end', () => {
        try {
          const responseData = responseBuffer.length === 1 ? responseBuffer[0] : Buffer.concat(responseBuffer);
          resolve(responseData);
          // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
        } catch (err) {
          resolve({ message: 'Unknown error' });
        }
      });
    });
  }

  private arrayBufferToString(buffer): string {
    return String.fromCharCode.apply(null, new Uint8Array(buffer));
  }

  private tryJsonParse(data) {
    try {
      return JSON.parse(data);
      // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    } catch (error) {}

    return data;
  }

  private async getData(response?: AxiosResponse): Promise<any> {
    const config = response?.config;

    try {
      switch (config?.responseType) {
        case 'arraybuffer': {
          const errorData = this.arrayBufferToString(response?.data);
          return this.tryJsonParse(errorData);
        }
        case 'stream': {
          const buffer = await this.getErrorDataFromStream(response?.data);
          const errorData = this.arrayBufferToString(buffer);
          return this.tryJsonParse(errorData);
        }
        default: {
          return response?.data;
        }
      }
      // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    } catch (err) {
      return response?.data;
    }
  }

  public canParse(error: GenericError): error is AxiosError<any> {
    return axios.isAxiosError(error);
  }

  public parse(error: GenericError): ErrorProviderError | any {
    if (!this.canParse(error)) {
      return error;
    }
    return error.response;
  }

  public async parseAsync(error: GenericError): Promise<ErrorProviderError | any> {
    if (!this.canParse(error)) {
      return error;
    }

    const { response } = error;
    const data = await this.getData(response);

    return { ...response, data };
  }
}
